import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { getProgressOverview, getTimeStatistics, getGrammarCategoryStats } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import XPBadge from './shared/XPBadge';
import Loading from './Loading';
import Error from './Error';

const Dashboard = ({ onBackToLessons }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [timeStats, setTimeStats] = useState(null);
  const [grammarStats, setGrammarStats] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [overviewData, timeData] = await Promise.all([
        getProgressOverview(),
        getTimeStatistics()
      ]);

      setOverview(overviewData);
      setTimeStats(timeData);

      // Load grammar stats separately (non-blocking)
      try {
        const grammarData = await getGrammarCategoryStats();
        setGrammarStats(grammarData);
      } catch (grammarErr) {
        console.error('Error loading grammar stats:', grammarErr);
      }
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError('Nepodařilo se načíst statistiky. Zkuste to prosím znovu.');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return '0 min';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes} min`;
  };

  if (loading) return <Loading />;
  if (error) return <Error message={error} />;
  if (!overview) return <Error message="Žádná data k zobrazení" />;

  // Prepare chart data for last 7 days
  const chartData = timeStats?.daily_time_last_7_days?.map(day => ({
    date: new Date(day.date).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'short' }),
    minutes: Math.round(day.seconds / 60)
  })) || [];

  return (
    <div style={{
      backgroundColor: '#f5f5f5',
      minHeight: '100vh',
      padding: '40px 20px'
    }}>
      <div className="container" style={{maxWidth: '1000px', margin: '0 auto'}}>
        {/* Tlačítko zpět */}
        <div style={{ marginBottom: '20px' }}>
          <button
            onClick={onBackToLessons}
            style={{
              backgroundColor: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              padding: '14px 24px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.borderColor = '#ccc';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.borderColor = '#e0e0e0';
            }}
          >
            <span style={{ fontSize: '1rem', fontWeight: '500', color: '#1a1a1a' }}>← Zpět na lekce</span>
          </button>
        </div>

        <h1 className="display-4 mb-4 text-dark text-center">Můj pokrok</h1>
        <p className="lead mb-6 text-center text-muted">Zobrazit statistiky vašeho pokroku v angličtině</p>

      {/* XP & Level karta */}
      {user?.total_xp != null && (
        <div className="card mb-4" style={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
          <div className="card-header" style={{ backgroundColor: '#f8f9fa', padding: '16px 20px' }}>
            <h2 className="h4 mb-0 text-dark" style={{ fontWeight: '500' }}>Moje úroveň</h2>
          </div>
          <div className="card-body text-center py-4">
            <XPBadge totalXp={user.total_xp} />
          </div>
        </div>
      )}

      {/* Overall Statistics */}
      <div className="row g-4 mb-4 justify-content-center">
        <div className="col-12 col-md-4 mb-3">
          <div className="card h-100">
            <div className="card-body text-center">
              <div className="text-sm text-muted mb-1">Dokončených cvičení</div>
              <div className="display-5 fw-bold text-primary">
                {overview.total_exercises_completed}
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-4 mb-3">
          <div className="card h-100">
            <div className="card-body text-center">
              <div className="text-sm text-muted mb-1">Celkový čas učení</div>
              <div className="display-5 fw-bold text-success">
                {formatTime(overview.total_time_spent_seconds)}
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-4 mb-3">
          <div className="card h-100">
            <div className="card-body text-center">
              <div className="text-sm text-muted mb-1">Průměrné skóre</div>
              <div className="display-5 fw-bold text-secondary">
                {overview.overall_average_score ?
                  `${overview.overall_average_score.toFixed(1)}%` :
                  'N/A'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Time Statistics Chart */}
      {chartData.length > 0 && (
        <div className="card mb-4" style={{border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden'}}>
          <div className="card-header" style={{backgroundColor: '#f8f9fa', padding: '16px 20px'}}>
            <h2 className="h4 mb-0 text-dark" style={{fontWeight: '500'}}>Čas učení (posledních 7 dní)</h2>
          </div>
          <div className="card-body" style={{padding: '20px'}}>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis label={{ value: 'Minuty', angle: -90, position: 'insideLeft' }} />
                <Tooltip
                  formatter={(value) => [`${value} min`, 'Čas']}
                  labelStyle={{ color: '#374151' }}
                />
                <Bar dataKey="minutes" fill="#0d6efd" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Grammar Category Stats */}
      {grammarStats && grammarStats.categories && grammarStats.categories.length > 0 && (
        <div className="card mb-4" style={{border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden'}}>
          <div className="card-header" style={{backgroundColor: '#f8f9fa', padding: '16px 20px'}}>
            <h2 className="h4 mb-0 text-dark" style={{fontWeight: '500'}}>Úspěšnost podle gramatiky</h2>
          </div>
          <div className="card-body" style={{padding: '20px'}}>
            {grammarStats.categories.map((cat) => {
              const color = cat.percentage >= 70 ? '#28a745' : cat.percentage >= 40 ? '#ffc107' : '#dc3545';
              return (
                <div key={cat.category} style={{ marginBottom: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontWeight: '500', fontSize: '0.95rem', color: '#333' }}>{cat.category}</span>
                    <span style={{ fontSize: '0.85rem', color: '#666' }}>
                      {cat.percentage}% ({cat.correct}/{cat.total})
                    </span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '10px',
                    backgroundColor: '#e9ecef',
                    borderRadius: '5px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${cat.percentage}%`,
                      height: '100%',
                      backgroundColor: color,
                      borderRadius: '5px',
                      transition: 'width 0.5s ease'
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Refresh Button */}
      <div className="mt-4 text-center">
        <button
          onClick={loadDashboardData}
          style={{
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 30px',
            fontSize: '1rem',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#0056b3';
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#007bff';
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = 'none';
          }}
        >
          Obnovit statistiky
        </button>
      </div>
      </div>
    </div>
  );
};

export default Dashboard;
